import React, { useState } from "react";
import withPass from "../../hooks/with_pass";
import { Clinic, PSConfig } from "../../store";
import { addDays, format, isSameDay, subDays } from "date-fns";
import { useQuery } from "@apollo/client";
import {
  get_wallet_transactions_by_clinic,
  get_wallet_summary,
  getWalletTransactionsByClinicVar,
  getWalletSummaryVar,
  GetWalletTransactionsByClinicData,
  GetWalletSummaryData,
  WalletTransactionRow,
} from "../../graphql/wallet_transaction";
import { get_clinic_members, getClinicMembersVar } from "../../graphql/clinic_member";
import LoadingSpinner from "../../components/loading_indicator";
import { Empty, Form, Row, Col, Table, Button, Card, Statistic } from "antd";
import Search from "antd/lib/input/Search";
import Error from "../../components/apollo_error";
import DateRange from "../../components/date_range";
import { formatMoney, formatPhoneNumber } from "../../helpers/utils";
import { FileExcelFilled } from "@ant-design/icons";
import { ExportExcel } from "../../helpers/excel";
import client from "../../graphql/apollo_client";

interface PassData {
  id?: string;
  env?: string;
  key?: string;
  refresh_token?: string;
  refresh_token_url?: string;
  url?: string;
  transfer_url?: string;
}

interface P {
  currentClinic?: Clinic;
  passConfig?: PSConfig;
}

const PAGE_SIZE = 20;

function Transactions(props: P) {
  const passCode =
    (() => {
      try {
        const raw = props.currentClinic?.pass;
        if (typeof raw !== "string") return "";
        const pass = JSON.parse(raw) as PassData;
        return pass?.id ?? "";
      } catch {
        return "";
      }
    })();

  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [ranges, setRanges] = useState({
    startDate: subDays(new Date(), 15),
    endDate: addDays(new Date(), 15),
  });
  const [exporting, setExporting] = useState(false);

  const getWalletDateStrings = () => {
    const fromDateStr = format(ranges.startDate, "yyyy-MM-dd");
    const toDateStr = isSameDay(ranges.startDate, ranges.endDate)
      ? format(addDays(ranges.endDate, 1), "yyyy-MM-dd")
      : format(ranges.endDate, "yyyy-MM-dd");
    return { fromDateStr, toDateStr };
  };

  const { fromDateStr, toDateStr } = getWalletDateStrings();

  const variables = getWalletTransactionsByClinicVar({
    clinicCode: passCode,
    skip: (page - 1) * PAGE_SIZE,
    take: PAGE_SIZE,
    fromDate: fromDateStr,
    toDate: toDateStr,
  });

  const { data, loading, error } = useQuery<GetWalletTransactionsByClinicData>(
    get_wallet_transactions_by_clinic,
    {
      variables,
      skip: !passCode,
    }
  );

  const summaryVariables = getWalletSummaryVar({
    clinicCode: passCode,
    fromDate: fromDateStr,
    toDate: toDateStr,
  });
  const { data: summaryData } = useQuery<GetWalletSummaryData>(
    get_wallet_summary,
    {
      variables: summaryVariables,
      skip: !passCode,
    }
  );

  const rows = data?.getWalletTransactionsByClinic?.data ?? [];
  const phoneNumbersWithoutName = [
    ...rows.filter((r) => !(r.senderName ?? "").trim()).map((r) => r.senderPhone).filter(Boolean),
    ...rows.filter((r) => !(r.recipientName ?? "").trim()).map((r) => r.recipientPhone).filter(Boolean),
  ].filter((p): p is string => !!p);
  const uniquePhones = Array.from(new Set(phoneNumbersWithoutName));

  const { data: clinicMembersData } = useQuery<{
    clinicMembers: Array<{ id: string; name: string | null; phonenumber: string; member: { id: string; name: string | null } }>;
  }>(get_clinic_members, {
    variables: getClinicMembersVar(props.currentClinic?.id ?? "", uniquePhones, passCode),
    skip: !props.currentClinic?.id || uniquePhones.length === 0,
  });

  const nameByPhone: Record<string, string> = {};
  (clinicMembersData?.clinicMembers ?? []).forEach((cm) => {
    const displayName = cm.name ?? cm.member?.name ?? "";
    if (displayName) nameByPhone[cm.phonenumber] = displayName;
  });

  const getDisplayName = (name: string | null | undefined, phone: string | null | undefined) =>
    (name?.trim() || (phone ? nameByPhone[phone] : "")) || "—";

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
    setPage(1);
  };

  const handleExport = async () => {
    const fileName =
      "wallet_transactions_" +
      format(ranges.startDate, "yyyy-MMM-dd") +
      "_" +
      format(ranges.endDate, "yyyy-MMM-dd");
    const header = [
      "Date",
      "Transaction Number",
      "Type",
      "Status",
      "Amount",
      "Comment",
      "Balance",
      "Sender Name",
      "Sender Phone",
      "Recipient Name",
      "Recipient Phone",
    ];

    setExporting(true);
    try {
      const { fromDateStr: fromDate, toDateStr: toDate } = getWalletDateStrings();
      const exportPageSize = 2000;
      let skip = 0;
      let allTransactions: WalletTransactionRow[] = [];

      for (;;) {
        const result = await client.query<GetWalletTransactionsByClinicData>({
          query: get_wallet_transactions_by_clinic,
          variables: getWalletTransactionsByClinicVar({
            clinicCode: passCode,
            skip,
            take: exportPageSize,
            fromDate,
            toDate,
          }),
        });

        const raw = result.data?.getWalletTransactionsByClinic;
        const chunk = raw?.data ?? [];
        allTransactions = allTransactions.concat(chunk);

        if (chunk.length < exportPageSize) break;
        skip += exportPageSize;
      }

      const exportPhonesWithoutName = Array.from(
        new Set([
          ...allTransactions.filter((t) => !(t.senderName ?? "").trim()).map((t) => t.senderPhone).filter(Boolean),
          ...allTransactions.filter((t) => !(t.recipientName ?? "").trim()).map((t) => t.recipientPhone).filter(Boolean),
        ])
      ) as string[];
      const exportNameByPhone: Record<string, string> = {};
      if (exportPhonesWithoutName.length > 0 && props.currentClinic?.id) {
        const membersResult = await client.query<{
          clinicMembers: Array<{ name: string | null; phonenumber: string; member: { name: string | null } }>;
        }>({
          query: get_clinic_members,
          variables: getClinicMembersVar(props.currentClinic.id, exportPhonesWithoutName, passCode),
        });
        (membersResult.data?.clinicMembers ?? []).forEach((cm) => {
          const displayName = cm.name ?? cm.member?.name ?? "";
          if (displayName) exportNameByPhone[cm.phonenumber] = displayName;
        });
      }

      const excelData = allTransactions.map((trx) => ({
        date: trx.createddate_myanmar ?? "",
        transaction_number: trx.transactionNumber ?? "",
        type: trx.type ?? "",
        status: trx.status ?? "",
        amount: Number(trx.balance ?? 0),
        comment: trx.comment ?? "",
        balance: Number(trx.accountbalance ?? 0),
        sender_name: (trx.senderName?.trim() || (trx.senderPhone ? exportNameByPhone[trx.senderPhone] : "")) ?? "",
        sender_phone: formatPhoneNumber(trx.senderPhone ?? "", props.currentClinic),
        recipient_name: (trx.recipientName?.trim() || (trx.recipientPhone ? exportNameByPhone[trx.recipientPhone] : "")) ?? "",
        recipient_phone: formatPhoneNumber(trx.recipientPhone ?? "", props.currentClinic),
      }));

      ExportExcel(excelData, fileName, header);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  if (!passCode) {
    return <Empty description="No pass configured for this clinic. Add pass to view wallet transactions." />;
  }
  if (loading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;

  const totalCount = data?.getWalletTransactionsByClinic?.totalCount ?? 0;

  const columns: any[] = [
    {
      title: "Date",
      dataIndex: "createddate_myanmar",
      key: "createddate_myanmar",
      editable: false,
      width: 200,
      defaultSortOrder: "descend" as const,
      sorter: (a: WalletTransactionRow, b: WalletTransactionRow) =>
        (a.createddate_myanmar ?? "").localeCompare(b.createddate_myanmar ?? ""),
      render: (_: unknown, r: WalletTransactionRow) => (
        <span style={{ whiteSpace: 'nowrap' }}>{r.createddate_myanmar ?? "—"}</span>
      ),
    },
    {
      title: "Transaction Number",
      dataIndex: "transactionNumber",
      key: "transactionNumber",
      editable: false,
      sorter: (a: WalletTransactionRow, b: WalletTransactionRow) =>
        (a.transactionNumber ?? "").localeCompare(b.transactionNumber ?? ""),
      render: (_: unknown, r: WalletTransactionRow) =>
        r.transactionNumber ?? "—",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: (a: WalletTransactionRow, b: WalletTransactionRow) =>
        (a.type ?? "").localeCompare(b.type ?? ""),
      render: (_: unknown, r: WalletTransactionRow) => r.type ?? "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a: WalletTransactionRow, b: WalletTransactionRow) =>
        (a.status ?? "").localeCompare(b.status ?? ""),
      render: (_: unknown, r: WalletTransactionRow) => r.status ?? "—",
    },
    {
      title: "Amount",
      dataIndex: "balance",
      key: "balance",
      editable: false,
      sorter: (a: WalletTransactionRow, b: WalletTransactionRow) =>
        Number(a.balance ?? 0) - Number(b.balance ?? 0),
      render: (_: unknown, r: WalletTransactionRow) =>
        formatMoney(Number(r.balance ?? 0)),
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      editable: false,
      sorter: (a: WalletTransactionRow, b: WalletTransactionRow) =>
        (a.comment ?? "").localeCompare(b.comment ?? ""),
      render: (_: unknown, r: WalletTransactionRow) => r.comment ?? "—",
    },
    {
      title: "Balance",
      dataIndex: "accountbalance",
      key: "accountbalance",
      editable: false,
      sorter: (a: WalletTransactionRow, b: WalletTransactionRow) =>
        Number(a.accountbalance ?? 0) - Number(b.accountbalance ?? 0),
      render: (_: unknown, r: WalletTransactionRow) =>
        formatMoney(Number(r.accountbalance ?? 0)),
    },
    {
      title: "Sender Name",
      dataIndex: "senderName",
      key: "senderName",
      sorter: (a: WalletTransactionRow, b: WalletTransactionRow) =>
        getDisplayName(a.senderName, a.senderPhone).localeCompare(getDisplayName(b.senderName, b.senderPhone)),
      render: (_: unknown, r: WalletTransactionRow) => getDisplayName(r.senderName, r.senderPhone),
    },
    {
      title: "Sender Phone",
      dataIndex: "senderPhone",
      key: "senderPhone",
      sorter: (a: WalletTransactionRow, b: WalletTransactionRow) =>
        (a.senderPhone ?? "").localeCompare(b.senderPhone ?? ""),
      render: (_: unknown, r: WalletTransactionRow) =>
        formatPhoneNumber(r.senderPhone ?? "", props.currentClinic) || "—",
    },
    {
      title: "Recipient Name",
      dataIndex: "recipientName",
      key: "recipientName",
      sorter: (a: WalletTransactionRow, b: WalletTransactionRow) =>
        getDisplayName(a.recipientName, a.recipientPhone).localeCompare(getDisplayName(b.recipientName, b.recipientPhone)),
      render: (_: unknown, r: WalletTransactionRow) => getDisplayName(r.recipientName, r.recipientPhone),
    },
    {
      title: "Recipient Phone",
      dataIndex: "recipientPhone",
      key: "recipientPhone",
      sorter: (a: WalletTransactionRow, b: WalletTransactionRow) =>
        (a.recipientPhone ?? "").localeCompare(b.recipientPhone ?? ""),
      render: (_: unknown, r: WalletTransactionRow) =>
        formatPhoneNumber(r.recipientPhone ?? "", props.currentClinic) || "—",
    },
  ];

  const filteredRows = searchText
    ? rows.filter(
        (r) =>
          (r.transactionNumber ?? "").toLowerCase().includes(searchText.toLowerCase()) ||
          getDisplayName(r.senderName, r.senderPhone).toLowerCase().includes(searchText.toLowerCase()) ||
          getDisplayName(r.recipientName, r.recipientPhone).toLowerCase().includes(searchText.toLowerCase())
      )
    : rows;

  const summary = summaryData?.getWalletSummary;

  const summaryCards = summary && (
    <Row gutter={16} style={{ marginBottom: 16 }}>
      <Col span={6}>
        <Card size="small">
          <Statistic title="Total In" value={summary.totalIn} formatter={(v: number) => formatMoney(Number(v))} />
        </Card>
      </Col>
      <Col span={6}>
        <Card size="small">
          <Statistic title="Total Out" value={summary.totalOut} formatter={(v: number) => formatMoney(Number(v))} />
        </Card>
      </Col>
      <Col span={6}>
        <Card size="small">
          <Statistic title="Transactions" value={summary.transactionCount} />
        </Card>
      </Col>
      <Col span={6}>
        <Card size="small">
          <Statistic title="Balance" value={summary.balance} formatter={(v: number) => formatMoney(Number(v))} />
        </Card>
      </Col>
    </Row>
  );

  const control = (
    <Row style={{ marginTop: -18 }}>
      <Col flex={4}>
        <Row>
          <Col span={8}>Transactions</Col>
          <Col span={8} offset={8}>
            <Search
              defaultValue={searchText ?? undefined}
              placeholder="search by transaction no, sender or recipient…"
              allowClear
              size="middle"
              onSearch={(val: string) => setSearchText(val || null)}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate}
          endDate={ranges.endDate}
          onSelect={onDateChange}
        />
      </Col>
      <Col flex={0} style={{ marginLeft: 8 }}>
        <Button
          size="middle"
          loading={exporting}
          icon={<FileExcelFilled />}
          onClick={() => handleExport()}
        >
          Export
        </Button>
      </Col>
    </Row>
  );

  const context = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form form={form} component={false}>
          <Table
            showHeader={filteredRows.length > 0}
            dataSource={filteredRows.map((b, idx) => ({
              key: b.transactionNumber ?? `row-${idx}-${b.createddate_myanmar ?? ""}`,
              ...b,
            }))}
            columns={columns}
            rowClassName="editable-row"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: searchText ? filteredRows.length : totalCount,
              onChange: setPage,
              showSizeChanger: false,
            }}
          />
        </Form>
      </Col>
    </Row>
  );

  return (
    <React.Fragment>
      {summaryCards}
      {control}
      {context}
    </React.Fragment>
  );
}

export default withPass(Transactions);
